import { useEffect, useState } from 'react'
import { CalendarCog, Send, ShieldAlert, UserPlus, X } from 'lucide-react'
import { ApiError, Button, EndpointHint, HelpIcon, LoadFailure, searchParam, TIER_LABELS, Skeleton, type SessionRole } from '../../core/index.js'
import { DEFAULT_RELEASE_RULES, schedulingRulesOf, useFacilityConfig, useUnitNames } from '../../facilities/index.js'
import { useAssignCandidates, useAssignSeat, useAvailableShifts, useBuildScheduleRun, useMonthHealth, useRemindAll, useRemindWorker, useScheduleBoard, useFacilityShiftPreferences, useReleaseScheduleRun, useScheduleRun, useSchedulePeriod, useSchedulePeriodWorkers } from '../hooks.js'
import type { AvailableShift, HealthFinding, PeriodWorkerReadiness, RunAssignCandidate, RemindAllResult, ScheduleBoardView, ScheduleRun, SchedulePeriodView, ShiftPreferences, WorkerRunResult } from '../types.js'
import { SEVERITY_DOT } from './monthHealthData.js'
import { MonthReadinessPanel } from './MonthReadinessPanel.js'
import { PublishedShiftsCalendar } from './PublishedShiftsCalendar.js'
import { ReadinessTable } from './ReadinessTable.js'
import { NO_SEAT_FILL, seatFillOf, type ReadinessDetail, type ReadinessFilter, type ReadinessTile, type SeatFill } from './monthReadinessData.js'
import { MonthStepper } from './MonthStepper.js'
import { describeDay, describePeriod, SHIFT_TYPE_LABELS } from './preferencesText.js'
import { CoverageGaps } from './CoverageGaps.js'
import { CONTEST_BASIS_LABEL, RUN_OUTCOME_CLASS, RUN_OUTCOME_LABEL, formatRunInstant } from './scheduleRunText.js'

/** "2 shifts asked for went to Maria Delgado: Mar 12 day, Mar 19 day" or nothing. */
function describeLost(result: WorkerRunResult, names: ReadonlyMap<string, string>): string | null {
  if (result.lost.length === 0) {
    return null
  }
  const to = [...new Set(result.lost.map((loss) => loss.toWorkerId).filter((id): id is string => id !== null))].map((id) => names.get(id) ?? id)
  return `${result.lost.length} ${result.lost.length === 1 ? 'shift' : 'shifts'} asked for went to ${to.length === 1 ? to[0] : 'others ranked ahead'}: ${result.lost.map((loss) => `${describeDay(loss.date)} ${SHIFT_TYPE_LABELS[loss.shiftType].toLowerCase()}`).join(', ')}`
}

/** Which tile's detail is open below, and the table's filter (which the pills change without changing the tile). */
/**
 * Who holds each seat: the released month as the board holds it once the board is read, else the run shown - the
 * released one, or the latest not yet released; no run, nobody.
 */
function seatFillFrom(run: ScheduleRun | null, board: ScheduleBoardView | null): SeatFill {
  if (board) {
    return seatFillOf('released', board.schedule)
  }
  if (!run) {
    return NO_SEAT_FILL
  }
  return seatFillOf(run.status === 'released' ? 'released' : 'run', run.results.map((result) => ({ name: result.name, assignments: result.awarded })))
}

type OpenDetail = { tile: ReadinessTile['key']; detail: ReadinessDetail; filter: ReadinessFilter }

/** Opening a tile shows its detail; pressing the open tile again hides it. */
function toggled(current: OpenDetail | null, tile: ReadinessTile): OpenDetail | null {
  return current?.tile === tile.key ? null : { tile: tile.key, detail: tile.opens.detail, filter: tile.opens.filter }
}

/** The detail a readiness tile opens, from data the caller read: the readiness table or the shifts calendar. */
export function ReadinessDetails({ open, onFilter, onRemind, onRemindAll, fill, period, timezone, workers, shifts, preferences, onClose }: { open: { detail: ReadinessDetail; filter: ReadinessFilter }; onFilter: (filter: ReadinessFilter) => void; onRemind?: (workerId: string) => Promise<void>; onRemindAll?: () => Promise<RemindAllResult>; /** Who holds each seat, for the calendar's filled and open seats. */ fill?: SeatFill; period: string; timezone: string; workers: { data: PeriodWorkerReadiness[] | undefined; isError: boolean; refetch: () => void; isFetching: boolean }; shifts: { data: AvailableShift[] | null | undefined; isError: boolean; refetch: () => void; isFetching: boolean }; preferences: ShiftPreferences[]; onClose: () => void }) {
  const query = open.detail === 'workers' ? workers : shifts
  return (
    <div className="border-t border-marine/10 pt-3 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-bold uppercase tracking-wide text-marine">
          {open.detail === 'workers' ? 'Who is ready' : 'Shifts published'} · {describePeriod(period)}
          <EndpointHint className="ml-2">{open.detail === 'workers' ? 'GET /facilities/{facilityId}/schedule/period/workers' : 'GET /facilities/{facilityId}/schedule/available-shifts'}</EndpointHint>
        </div>
        <button type="button" onClick={onClose} className="text-[11px] font-bold text-marine hover:text-marine-dk">
          Hide
        </button>
      </div>
      {query.isError ? (
        <LoadFailure size="xs" message={open.detail === 'workers' ? 'The workers could not be loaded.' : 'The shifts could not be loaded.'} onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : query.data === undefined ? (
        <Skeleton />
      ) : open.detail === 'workers' ? (
        <ReadinessTable workers={workers.data ?? []} period={period} timezone={timezone} filter={open.filter} onFilter={onFilter} onRemind={onRemind} onRemindAll={onRemindAll} />
      ) : (
        <PublishedShiftsCalendar shifts={shifts.data ?? []} preferences={preferences} period={period} fill={fill} />
      )}
    </div>
  )
}

const RULE_LABEL: Record<RunAssignCandidate['wouldBreak'][number], string> = { day_after_evening: 'a day after an evening', max_consecutive_days: 'days in a row', over_week_max: 'the week’s shift cap', weekly_overtime: 'weekly overtime' }

/** "Sun, Nov 1 Day · Unit A · RN": the unit by the facility's name for it, never its id. */
function describeSeat(shift: AvailableShift, units: ReadonlyMap<string, string>): string {
  return `${describeDay(shift.date)} ${SHIFT_TYPE_LABELS[shift.period as keyof typeof SHIFT_TYPE_LABELS] ?? shift.period} · ${units.get(shift.unitId) ?? shift.unitId}${shift.role ? ` · ${shift.role}` : ''}${shift.seat && shift.seat > 1 ? ` · seat ${shift.seat}` : ''}`
}

/**
 * Who could take one unfilled seat (month health plan §5): everyone the hard rules admit, in rank order, with the
 * hours the week would hold; a placement that passes a month rule asks for a reason code first.
 */
function AssignSeatChooser({ facilityId, runId, shift, onDone }: { facilityId: string; runId: string; shift: AvailableShift; onDone: () => void }) {
  const candidates = useAssignCandidates(facilityId, runId, shift.id)
  const units = useUnitNames(facilityId)
  const assign = useAssignSeat(facilityId, runId)
  const [chosen, setChosen] = useState<string | null>(null)
  const [reasonCode, setReasonCode] = useState('')
  const [note, setNote] = useState('')
  const picked = candidates.data?.candidates.find((candidate) => candidate.workerId === chosen) ?? null
  const needsReason = (picked?.wouldBreak.length ?? 0) > 0
  const failure = assign.isError ? (assign.error instanceof ApiError ? assign.error.message : 'The seat could not be assigned. Try again.') : null

  function place(candidate: RunAssignCandidate): void {
    if (candidate.wouldBreak.length > 0 && chosen !== candidate.workerId) {
      setChosen(candidate.workerId)
      return
    }
    assign.mutate({ shiftId: shift.id, workerId: candidate.workerId, ...(reasonCode.trim() ? { reasonCode: reasonCode.trim().toUpperCase() } : {}), ...(note.trim() ? { note: note.trim() } : {}) }, { onSuccess: onDone })
  }

  return (
    <div className="mt-2 flex flex-col gap-2 rounded border border-marine/15 bg-canvas p-3">
      <div className="flex items-center justify-between gap-2 text-[10px] font-bold uppercase tracking-wide text-marine">
        <span>Who could take {describeSeat(shift, units)}</span>
        <button type="button" className="text-ink-mid hover:underline normal-case tracking-normal font-normal" onClick={onDone}>
          Close
        </button>
      </div>
      {candidates.isPending ? (
        <Skeleton label="Loading the roster" />
      ) : candidates.isError ? (
        <LoadFailure size="xs" message="The candidates could not be read." onRetry={() => void candidates.refetch()} retrying={candidates.isFetching} />
      ) : candidates.data.candidates.length === 0 ? (
        <p className="text-[11px] text-ink-mid">Nobody on the roster may take this seat: the role, the unit, a credential, or a shift that day already.</p>
      ) : (
        <ul className="divide-y divide-marine/10">
          {candidates.data.candidates.map((candidate) => (
            <li key={candidate.workerId} className="flex flex-col gap-1 py-1.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs text-ink">
                  <span className="font-bold">{candidate.name}</span> <span className="text-ink-mid">· {TIER_LABELS[candidate.tier]} · {candidate.score} · {candidate.role}</span>
                  <div className="text-[10px] text-ink-mid">
                    {candidate.weekHours} h that week, {candidate.weekHoursAfter} h with this shift · {candidate.shiftsThisWeek} {candidate.shiftsThisWeek === 1 ? 'shift' : 'shifts'}
                    {candidate.wouldBreak.length > 0 && <span className="text-coral"> · passes {candidate.wouldBreak.map((rule) => RULE_LABEL[rule]).join(', ')}</span>}
                  </div>
                </div>
                <Button icon={UserPlus} size="sm" variant={candidate.wouldBreak.length > 0 ? 'secondary' : 'primary'} disabled={assign.isPending || (candidate.wouldBreak.length > 0 && chosen === candidate.workerId && reasonCode.trim() === '')} onClick={() => place(candidate)}>
                  {candidate.wouldBreak.length > 0 && chosen !== candidate.workerId ? 'Assign with a reason…' : assign.isPending && assign.variables?.workerId === candidate.workerId ? 'Assigning…' : 'Assign'}
                </Button>
              </div>
              {needsReason && chosen === candidate.workerId && (
                <div className="flex flex-wrap items-end gap-2">
                  <label className="flex flex-col gap-0.5 text-[10px] font-bold uppercase tracking-wide text-ink-mid">
                    Reason code
                    <input className="rounded border border-marine/20 px-2 py-1 text-xs font-normal normal-case tracking-normal text-ink" value={reasonCode} onChange={(event) => setReasonCode(event.target.value)} maxLength={80} placeholder="NO_ONE_ELSE" />
                  </label>
                  <label className="flex min-w-[12rem] flex-1 flex-col gap-0.5 text-[10px] font-bold uppercase tracking-wide text-ink-mid">
                    Note
                    <input className="rounded border border-marine/20 px-2 py-1 text-xs font-normal normal-case tracking-normal text-ink" value={note} onChange={(event) => setNote(event.target.value)} maxLength={500} placeholder="Optional" />
                  </label>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {failure && (
        <p role="alert" className="text-[11px] text-coral">
          {failure}
        </p>
      )}
    </div>
  )
}

/** How many unfilled seats are listed before the rest fold. */
const SEATS_SHOWN = 12

/** The run's unfilled seats as a list, each with Assign while the run is not released and the viewer may fill it. */
function UnfilledSeats({ run, facilityId, defaultOpen = false }: { run: ScheduleRun; facilityId: string | null; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  const [showAll, setShowAll] = useState(false)
  const [assigning, setAssigning] = useState<string | null>(null)
  const canAssign = facilityId !== null && run.status !== 'released'
  const units = useUnitNames(facilityId)
  const seats = showAll ? run.unfilled : run.unfilled.slice(0, SEATS_SHOWN)
  if (run.unfilled.length === 0) {
    return null
  }
  if (!open) {
    return (
      <p className="text-[11px] text-ink-mid">
        <span className="font-bold text-coral">{run.unfilled.length} unfilled:</span> {run.unfilled.slice(0, 12).map((shift) => `${describeDay(shift.date)} ${SHIFT_TYPE_LABELS[shift.period as keyof typeof SHIFT_TYPE_LABELS] ?? shift.period}`).join(', ')}
        {run.unfilled.length > 12 && ` and ${run.unfilled.length - 12} more`}
        {' · '}
        <button type="button" className="font-bold text-marine hover:underline" onClick={() => setOpen(true)}>
          {canAssign ? 'Assign seats' : 'Show'}
        </button>
      </p>
    )
  }
  return (
    <div id="unfilled-seats" className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2 text-[11px]">
        <span className="font-bold text-coral">{run.unfilled.length} unfilled {run.unfilled.length === 1 ? 'seat' : 'seats'}</span>
        <button type="button" className="text-marine hover:underline" onClick={() => setOpen(false)}>
          Hide
        </button>
      </div>
      {canAssign && <p className="text-[10px] text-ink-mid">A seat filled here is the worker’s before the release; the hard rules never pass, and a month rule passes with a reason that the run keeps. Every seat left goes to the open board at the release.</p>}
      <ul className="divide-y divide-marine/10">
        {seats.map((shift) => (
          <li key={shift.id} className="py-1.5">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ink">
              <span>{describeSeat(shift, units)}</span>
              {canAssign && assigning !== shift.id && (
                <Button icon={UserPlus} size="sm" variant="secondary" onClick={() => setAssigning(shift.id)}>
                  Assign
                </Button>
              )}
            </div>
            {canAssign && assigning === shift.id && facilityId && <AssignSeatChooser facilityId={facilityId} runId={run.runId} shift={shift} onDone={() => setAssigning(null)} />}
          </li>
        ))}
      </ul>
      {run.unfilled.length > SEATS_SHOWN && (
        <button type="button" className="self-start text-[11px] font-bold text-marine hover:underline" onClick={() => setShowAll((current) => !current)}>
          {showAll ? 'Show fewer' : `Show ${run.unfilled.length - SEATS_SHOWN} more`}
        </button>
      )}
    </div>
  )
}

/** One run in full: every worker's result, the contests, the unfilled shifts - with Assign on each while `facilityId` is given and the run is not released. */
export function ScheduleRunResults({ run, facilityId = null, openUnfilled = false }: { run: ScheduleRun; /** Given by the Tools card: the seats take a worker; left out (the admin's dialog, the simulator) they are read-only. */ facilityId?: string | null; /** Opens the unfilled list at once (`?detail=unfilled`, the Month card's Assign the seats). */ openUnfilled?: boolean }) {
  const [showContests, setShowContests] = useState(false)
  const names = new Map(run.results.map((result) => [result.workerId, result.name]))
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-mid">
        <span>
          <span className="font-bold text-ink">{run.totals.scheduled}</span> scheduled · <span className="font-bold text-ink">{run.totals.assigned}</span> assigned
          {run.totals.noPreferences > 0 && (
            <>
              {' '}
              · <span className="font-bold text-coral">{run.totals.noPreferences}</span> sent no preferences
            </>
          )}
          {run.totals.nothingFit > 0 && (
            <>
              {' '}
              · <span className="font-bold text-coral">{run.totals.nothingFit}</span> with nothing that fit
            </>
          )}
        </span>
        <span>
          <span className="font-bold text-ink">{run.totals.shiftsFilled}</span> of {run.totals.shiftsPublished} shifts filled · {run.totals.contests} contested
        </span>
        <span>
          Built {formatRunInstant(run.ranAt)}
          {run.builtAtClose && ' · by the engine when preferences closed'}
          {run.beforeClose && ' · before preferences closed'}
          {run.releasedAt && ` · released ${formatRunInstant(run.releasedAt)}`}
        </span>
      </div>
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-left text-[9px] font-bold uppercase tracking-wide text-ink-mid">
            <th className="py-1 pr-2 font-bold">Worker</th>
            <th className="py-1 pr-2 font-bold">Rank</th>
            <th className="py-1 pr-2 font-bold text-right">Asked</th>
            <th className="py-1 pr-2 font-bold text-right">Got</th>
            <th className="py-1 pr-2 font-bold text-right">Hours</th>
            <th className="py-1 font-bold">Outcome</th>
          </tr>
        </thead>
        <tbody>
          {run.results.map((result) => {
            const lost = describeLost(result, names)
            return (
              <tr key={result.workerId} className="border-t border-marine/10 align-top">
                <td className="py-1.5 pr-2 text-ink">{result.name}</td>
                <td className="py-1.5 pr-2 text-ink-mid">
                  {TIER_LABELS[result.tier]} · {Math.round(result.score)}
                </td>
                <td className="py-1.5 pr-2 text-right text-ink-mid">{result.asked}</td>
                <td className="py-1.5 pr-2 text-right font-bold text-ink">{result.awarded.length}</td>
                <td className="py-1.5 pr-2 text-right text-ink">{result.hours}</td>
                <td className="py-1.5 text-[10px]">
                  <span className={`font-bold ${RUN_OUTCOME_CLASS[result.outcome]}`}>{RUN_OUTCOME_LABEL[result.outcome]}</span>
                  {result.preferencesSource === 'previous_month' && <span className="block text-[10px] text-ink-mid">Sent none: last month&apos;s preferences used</span>}
                  {result.preferencesSource === 'none' && <span className="block text-[10px] text-coral">Sent none: assigned last</span>}
                  {result.flags.includes('under_target') && <span className="text-coral"> · under target</span>}
                  {result.flags.includes('pto_day_denied') && <span className="text-ink-mid"> · {result.ptoDecisions.filter((entry) => entry.status === 'denied').length > 1 ? 'PTO days denied' : 'a PTO day denied'}</span>}
                  {result.flags.includes('medium_day_override') && <span className="text-ink-mid"> · Medium days overridden</span>}
                  {(result.takenDaysOff ?? []).length > 0 && <div className="text-coral">Day off taken for coverage: {(result.takenDaysOff ?? []).map((entry) => `${describeDay(entry.date)} (${entry.priority})`).join(', ')}</div>}
                  {lost && <div className="text-ink-mid">{lost}</div>}
                  {result.ptoDecisions.some((entry) => entry.reason === 'below_minimum') && <div className="text-ink-mid">PTO denied where staffing would fall below its minimum: {result.ptoDecisions.filter((entry) => entry.reason === 'below_minimum').map((entry) => describeDay(entry.date)).join(', ')}</div>}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {run.contests.length > 0 && (
        <div>
          <button type="button" onClick={() => setShowContests((value) => !value)} className="text-[11px] font-bold text-marine hover:text-marine-dk">
            {showContests ? 'Hide' : 'Show'} the {run.contests.length} contested {run.contests.length === 1 ? 'shift' : 'shifts'}
          </button>
          {showContests && (
            <ul className="mt-1 flex flex-col gap-0.5 text-[11px] text-ink-mid">
              {run.contests.map((contest) => (
                <li key={contest.shiftId}>
                  {describeDay(contest.date)} {SHIFT_TYPE_LABELS[contest.shiftType].toLowerCase()}: {contest.candidates.map((candidate) => names.get(candidate.workerId) ?? candidate.workerId).join(', ')} to{' '}
                  <span className="font-bold text-ink">{(contest.awarded ?? (contest.awardedTo ? [contest.awardedTo] : [])).map((id) => names.get(id) ?? id).join(', ') || 'nobody'}</span>
                  {(contest.capacity ?? 1) > 1 && ` (${contest.capacity} seats)`}
                  {contest.basis && ` (${CONTEST_BASIS_LABEL[contest.basis]})`}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {run.coverage && <CoverageGaps linesApplied={run.coverage.linesApplied} gaps={run.coverage.gaps} basisLabel="this run" />}
      {(run.assignments?.length ?? 0) > 0 && (
        <p className="text-[11px] text-ink-mid">
          <span className="font-bold text-marine">{run.assignments?.length} {run.assignments?.length === 1 ? 'seat' : 'seats'} assigned by hand</span>
          {run.assignments?.some((entry) => entry.overriddenRules.length > 0) && ` · ${run.assignments.filter((entry) => entry.overriddenRules.length > 0).length} past a month rule with a reason`}
        </p>
      )}
      <UnfilledSeats run={run} facilityId={facilityId} defaultOpen={openUnfilled} />
    </div>
  )
}

/**
 * The "Build the month" card on the Dashboard's Month tab, Build and release (scheduling rules plan phase B):
 * where the month stands by the facility's rules, one button that drafts
 * every worker in rank order and settles the contests, the result worker by
 * worker, and Release, which makes the run the month and tells everyone
 * their part. Staff only; a partner reads the same through its own tab.
 */
/** The findings a refused release named, from the API's answer; none for any other failure. */
function refusedFindings(error: unknown): HealthFinding[] {
  if (!(error instanceof ApiError) || (error.status !== 409 && error.status !== 403)) {
    return []
  }
  const details = error.details as { findings?: HealthFinding[] } | undefined
  return Array.isArray(details?.findings) ? details.findings : []
}

/**
 * Release, under the release guard (month health plan §4): the blocks of the month's health that hold it, in coral,
 * and - for whoever the facility's release rules admit - Release anyway with a reason code, which the run keeps.
 * Everyone else is told who may. With nothing blocking, the one Release button.
 */
function ReleaseControls({ facilityId, period, runId, role, release }: { facilityId: string; period: string | null; runId: string; role: SessionRole; release: ReturnType<typeof useReleaseScheduleRun> }) {
  const health = useMonthHealth(facilityId, period, false)
  const config = useFacilityConfig(facilityId)
  const [overriding, setOverriding] = useState(false)
  const [reasonCode, setReasonCode] = useState('')
  const [note, setNote] = useState('')
  const rules = schedulingRulesOf(config.data ?? null).release ?? DEFAULT_RELEASE_RULES
  const fromAnswer = refusedFindings(release.error)
  const blocks = fromAnswer.length > 0 ? fromAnswer : (health.data?.findings ?? []).filter((finding) => finding.severity === 'block')
  const mayOverride = rules.override === 'staff' || (rules.override === 'operator' && role === 'operator')
  if (blocks.length === 0) {
    return (
      <Button icon={Send} size="sm" disabled={release.isPending} onClick={() => release.mutate({ runId })}>
        {release.isPending ? 'Releasing…' : 'Release to workers'}
      </Button>
    )
  }
  const whoMay = rules.override === 'none' ? 'This facility releases past no block: clear them first.' : rules.override === 'operator' ? 'Your operator may release past them with a reason.' : null
  return (
    <div className="flex w-full flex-col gap-2 rounded border border-coral/30 bg-coral-lt/40 p-3">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-coral">
        <ShieldAlert className="h-3.5 w-3.5" aria-hidden />
        {blocks.length === 1 ? 'One finding holds the release' : `${blocks.length} findings hold the release`}
      </div>
      <ul className="flex flex-col gap-1">
        {blocks.map((finding, index) => (
          <li key={`${finding.key}-${index}`} className="flex items-start gap-2 text-xs text-ink">
            <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEVERITY_DOT.block}`} aria-hidden />
            {finding.message}
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-ink-mid">The Month card on the Dashboard has the button that fixes each.{!mayOverride && whoMay ? ` ${whoMay}` : ''}</p>
      {mayOverride && !overriding && (
        <div>
          <Button icon={ShieldAlert} size="sm" variant="secondary" onClick={() => setOverriding(true)}>
            Release anyway…
          </Button>
        </div>
      )}
      {mayOverride && overriding && (
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            if (reasonCode.trim()) {
              release.mutate({ runId, override: { reasonCode: reasonCode.trim().toUpperCase(), ...(note.trim() ? { note: note.trim() } : {}) } })
            }
          }}
        >
          <label className="flex flex-col gap-1 text-[10px] font-bold uppercase tracking-wide text-ink-mid">
            Reason code
            <input className="rounded border border-marine/20 px-2 py-1 text-xs font-normal normal-case tracking-normal text-ink" value={reasonCode} onChange={(event) => setReasonCode(event.target.value)} maxLength={40} placeholder="AGENCY_BOOKED" required />
          </label>
          <label className="flex flex-col gap-1 text-[10px] font-bold uppercase tracking-wide text-ink-mid">
            Note
            <input className="rounded border border-marine/20 px-2 py-1 text-xs font-normal normal-case tracking-normal text-ink" value={note} onChange={(event) => setNote(event.target.value)} maxLength={500} placeholder="What covers it" />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" icon={Send} size="sm" disabled={release.isPending || reasonCode.trim() === ''}>
              {release.isPending ? 'Releasing…' : 'Release past these, with this reason'}
            </Button>
            <Button icon={X} size="sm" variant="secondary" onClick={() => setOverriding(false)}>
              Cancel
            </Button>
          </div>
          <p className="text-[10px] text-ink-mid">The reason and the blocks are kept on the run and in the facility’s activity feed.</p>
        </form>
      )}
    </div>
  )
}

export function ScheduleRunCard({ facilityId, hasWorkers = true, onPeriodChange, role = 'operator', onPublishShifts }: { facilityId: string; hasWorkers?: boolean; /** Opens the Publish shifts tool, for a month that waits on its shifts alone. */ onPublishShifts?: () => void; /** Told the month shown, so the Tools tab's board card follows it. */ onPeriodChange?: (period: string) => void; /** Who is looking: whether Release anyway is theirs under the facility's release rules. */ role?: SessionRole }) {
  const [period, setPeriod] = useState<string | null>(null)
  const [open, setOpen] = useState<OpenDetail | null>(null)
  // The Month card's Assign the seats opens Tools on this card with the unfilled list already open.
  const [openUnfilled] = useState(() => searchParam('detail') === 'unfilled')
  const periodQuery = useSchedulePeriod(facilityId, period)
  const view = periodQuery.data ?? null
  const shownPeriod = view?.period ?? null
  useEffect(() => {
    if (shownPeriod) {
      onPeriodChange?.(shownPeriod)
    }
  }, [shownPeriod, onPeriodChange])
  const workersQuery = useSchedulePeriodWorkers(facilityId, shownPeriod, open?.detail === 'workers')
  const shiftsQuery = useAvailableShifts(open?.detail === 'shifts' ? facilityId : null)
  const preferencesQuery = useFacilityShiftPreferences(open?.detail === 'shifts' ? facilityId : null, shownPeriod)
  const remind = useRemindWorker(facilityId, shownPeriod)
  const remindAll = useRemindAll(facilityId, shownPeriod)
  // The released month's board holds the swaps and pickups since the release; read only while the calendar is open.
  const boardQuery = useScheduleBoard(open?.detail === 'shifts' && view?.status === 'released' ? facilityId : null, shownPeriod)
  const shownRun = view?.releasedRun ?? view?.latestRun ?? null
  const runQuery = useScheduleRun(facilityId, shownRun?.runId ?? null)
  const build = useBuildScheduleRun(facilityId)
  const release = useReleaseScheduleRun(facilityId)
  const failure = build.isError ? (build.error instanceof ApiError ? build.error.message : 'The month could not be built. Try again.') : release.isError && refusedFindings(release.error).length === 0 ? (release.error instanceof ApiError ? release.error.message : 'The run could not be released. Try again.') : null
  const buildBlocked = !hasWorkers ? 'No workers on the roster yet' : view?.shiftsPublished === 0 ? 'Nothing is published for this month' : view?.status === 'released' ? 'This month is released' : null

  return (
    <div className="bg-white border border-marine/15 rounded p-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-black text-ink">
            Build the month
            <HelpIcon topic="schedule.build-the-month" className="ml-1" />
                        <EndpointHint className="ml-2">POST /facilities/{'{facilityId}'}/schedule/runs</EndpointHint>
          </div>
          <p className="text-[11px] text-ink-mid max-w-xl leading-relaxed">
            Every worker drafted under this facility's scheduling rules, in rank order - tier, then score, then seniority - so a shift two people asked for stays with the one ranked ahead and the next is drafted around it. Nothing reaches a worker until you release the run.
          </p>
        </div>
        {/* A released month is not built again: what changes after the release is the board's. */}
        {view?.status !== 'released' && (
          <div className="flex items-center gap-2">
            <Button icon={CalendarCog} disabled={build.isPending || !view || buildBlocked !== null} title={buildBlocked ?? undefined} onClick={() => build.mutate(view?.period ?? null)}>
              {build.isPending ? 'Building…' : view?.latestRun ? 'Build again' : 'Build the month'}
            </Button>
          </div>
        )}
      </div>
      {periodQuery.isPending ? (
        <Skeleton />
      ) : periodQuery.isError || !view ? (
        <LoadFailure size="xs" message="The month could not be loaded." onRetry={() => void periodQuery.refetch()} retrying={periodQuery.isFetching} />
      ) : (
        <MonthReadinessPanel view={view} stepper={<MonthStepper size="lg" value={view.period} onChange={(choice) => { setPeriod(choice); setOpen(null) }} />} activeTile={open?.tile ?? null} canRemind onOpen={(tile) => setOpen(toggled(open, tile))} onPublishShifts={onPublishShifts} />
      )}
      {view && open && (
        <ReadinessDetails
          open={open}
          onFilter={(filter) => setOpen((current) => (current ? { ...current, filter } : current))}
          onRemind={async (workerId) => {
            await remind.mutateAsync(workerId)
          }}
          onRemindAll={() => remindAll.mutateAsync()}
          fill={seatFillFrom(runQuery.data ?? null, boardQuery.data ?? null)}
          period={view.period}
          timezone={view.timezone}
          workers={{ data: workersQuery.data?.workers, isError: workersQuery.isError, refetch: () => void workersQuery.refetch(), isFetching: workersQuery.isFetching }}
          shifts={{ data: shiftsQuery.data, isError: shiftsQuery.isError, refetch: () => void shiftsQuery.refetch(), isFetching: shiftsQuery.isFetching }}
          preferences={preferencesQuery.data?.preferences ?? []}
          onClose={() => setOpen(null)}
        />
      )}
      {failure && (
        <p role="alert" className="text-[11px] text-coral">
          {failure}
        </p>
      )}
      {shownRun && (
        <div className="border-t border-marine/10 pt-3 flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[10px] font-bold uppercase tracking-wide text-marine">
              {shownRun.status === 'released' ? 'The released month' : 'Latest run, not yet released'}
              {shownRun.releasedOver && <span className="ml-2 rounded-full border border-coral/40 bg-coral-lt px-2 py-0.5 text-[9px] text-coral" title={shownRun.releasedOver.findings.map((finding) => finding.message).join('; ')}>Released past {shownRun.releasedOver.findings.length === 1 ? 'a block' : `${shownRun.releasedOver.findings.length} blocks`} · {shownRun.releasedOver.reasonCode}</span>}
            </div>
            {shownRun.status !== 'released' && <ReleaseControls facilityId={facilityId} period={shownPeriod} runId={shownRun.runId} role={role} release={release} />}
          </div>
          {runQuery.isPending ? <Skeleton label="Loading the run" /> : runQuery.isError || !runQuery.data ? <LoadFailure size="xs" message="The run could not be loaded." onRetry={() => void runQuery.refetch()} retrying={runQuery.isFetching} /> : <ScheduleRunResults run={runQuery.data} facilityId={facilityId} openUnfilled={openUnfilled} />}
        </div>
      )}
    </div>
  )
}

/**
 * A read-only view of a facility's month and its released or latest run: the admin's facility dialog and the Partner
 * Simulator. With `workers` (the admin, who reads them) the tiles open the readiness table and, with `shifts`, the
 * calendar; without, they are figures only - a partner reads the counts, not who they are (plan R3).
 */
export function ScheduleRunReadOnly({ view, run, workers, shifts, preferences = [] }: { view: SchedulePeriodView; run: ScheduleRun | null; workers?: PeriodWorkerReadiness[]; shifts?: AvailableShift[] | null; preferences?: ShiftPreferences[] }) {
  const [open, setOpen] = useState<OpenDetail | null>(null)
  const loaded = { isError: false, refetch: () => undefined, isFetching: false }
  return (
    <div className="flex flex-col gap-3">
      <MonthReadinessPanel view={view} activeTile={open?.tile ?? null} onOpen={workers ? (tile) => setOpen(tile.opens.detail === 'shifts' && shifts === undefined ? null : toggled(open, tile)) : undefined} />
      {open && <ReadinessDetails open={open} fill={seatFillFrom(run, null)} onFilter={(filter) => setOpen((current) => (current ? { ...current, filter } : current))} period={view.period} timezone={view.timezone} workers={{ ...loaded, data: workers }} shifts={{ ...loaded, data: shifts ?? [] }} preferences={preferences} onClose={() => setOpen(null)} />}
      {run ? <ScheduleRunResults run={run} /> : <p className="text-[11px] text-ink-mid">No run built for {describePeriod(view.period)} yet.</p>}
    </div>
  )
}
